import { COMPANY_ID } from '@constants/auth.constant';
import { useAuth } from '@context/Auth';
import { IObject } from '@interface/common.interface';
import { LocalStorageService } from '@services/utils/localStorage.service';
import { ReactNode } from 'react';

// Roles that bypass feature gating and see everything, as in hrm, desk and labor.
const SUPER_ROLES = ['COMPANY_ADMIN'];

/** The four flags of an auth.role_features grant. `create` is the can_save column. */
export type PermAction = 'show' | 'create' | 'update' | 'delete';
export type FeaturePerms = Record<PermAction, boolean>;

const ALL_PERMS: FeaturePerms = { show: true, create: true, update: true, delete: true };
const NO_PERMS: FeaturePerms = { show: false, create: false, update: false, delete: false };

/**
 * Reads the user's granted features (portal verify-token → getUserAppAcl, stored on userInfo) and
 * answers what a screen offers. stock keys a screen by auth.features.CODE (ROUTE_CODES), where the
 * other apps key by url.
 *
 * Show is implied by any other flag, as on the server (core PermissionService) and on portal's Role
 * Permission screen. While verify-token is in flight (isChecking, splash shown) everything is
 * allowed so nobody is locked out mid-load; once it has answered, a feature that is absent is
 * DENIED. COMPANY_ADMIN may do everything, so retired screens must be removed from the menu and
 * routes rather than hidden behind a code that has no feature row.
 *
 * This decides what the UI offers. It is not the control: store-service refuses with a 403 itself.
 */
export const usePermissions = () => {
  const { userInfo, isChecking } = useAuth();

  // Role is per-company: resolve it from the selected company, as hrm does.
  const currentCid = LocalStorageService.get(COMPANY_ID);
  const currentCompany =
    userInfo?.companies?.find((c) => c.companyId === currentCid) || userInfo?.companies?.[0];
  const roleKey = currentCompany?.roleKey || currentCompany?.roleName;
  const isSuperAdmin = !!roleKey && SUPER_ROLES.includes(roleKey);
  const features = userInfo?.features;
  const loaded = Array.isArray(features);

  // One row per feature (verify-token groups by feature id), keyed by code.
  const byCode = new Map<string, IObject>();
  (features || []).forEach((f) => f?.code && byCode.set(f.code, f));

  /** What the user may do on the screen whose ROUTE_CODES value is `code`. */
  const perms = (code?: string): FeaturePerms => {
    if (isSuperAdmin || isChecking) return ALL_PERMS;
    const f = loaded && code ? byCode.get(code) : undefined;
    if (!f) return NO_PERMS;
    const create = Number(f.canSave) === 1;
    const update = Number(f.canUpdate) === 1;
    const del = Number(f.canDelete) === 1;
    return { show: Number(f.canShow) === 1 || create || update || del, create, update, delete: del };
  };

  const can = (code: string | undefined, action: PermAction): boolean => perms(code)[action];

  /**
   * Menu and route visibility. A MISSING code means the screen is open to everyone: some children
   * are deliberately ungated. Every gated one must name a real auth.features row.
   */
  const canSee = (code?: string): boolean => !code || can(code, 'show');

  return { isSuperAdmin, roleKey, features: features || [], loaded, perms, can, canSee };
};

/**
 * Renders children only when the user may do `action` on the screen `feature` (its ROUTE_CODES
 * value), otherwise `fallback` (nothing by default). For a single button; a list page reads
 * perms(ROUTE_CODES.X) once and passes handlers conditionally instead.
 */
export const Can = ({
  feature,
  action,
  children,
  fallback = null,
}: {
  feature?: string;
  action: PermAction;
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const { can } = usePermissions();
  return <>{can(feature, action) ? children : fallback}</>;
};
